/**
 * SectionHeader — reusable "Title · See all" row used between rails.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { css } from './tokens';

export interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  /** Accent stripe on the left of the title. Defaults to brand primary. */
  accent?: 'primary' | 'accent' | 'food' | 'rides' | null;
  /** Optional right-side action ("See all", filter button, etc). */
  actionLabel?: string;
  onAction?: () => void;
  /** Replace the action with a custom node (e.g. a chip group). */
  trailing?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

const ACCENT_MAP: Record<NonNullable<SectionHeaderProps['accent']>, string> = {
  primary: css.color.primary,
  accent:  css.color.accent,
  food:    css.color.food,
  rides:   css.color.rides,
};

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  subtitle,
  accent = 'primary',
  actionLabel,
  onAction,
  trailing,
  style,
  className,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        margin: '16px 0 10px',
        ...style,
      }}
      className={className}
    >
      <div>
        <h2
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            margin: 0,
            fontFamily: css.font.display,
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: '-0.015em',
            color: css.color.text,
          }}
        >
          {accent && (
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 4,
                height: 18,
                borderRadius: 3,
                background: `linear-gradient(180deg, ${ACCENT_MAP[accent]}, ${css.color.accent})`,
              }}
            />
          )}
          {title}
        </h2>
        {subtitle && (
          <div
            style={{
              fontSize: 12,
              color: css.color.textMuted,
              marginTop: 2,
              marginLeft: accent ? 14 : 0,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {trailing ?? (actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            background: 'transparent',
            border: 'none',
            color: css.color.primary,
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {actionLabel} <ChevronRight size={14} />
        </button>
      ))}
    </div>
  );
};

export default SectionHeader;

